import { isRejectedWithValue, PayloadAction, SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import { i18n } from '@client/toolkit';
import { generatedApiSlice, ProblemDetails } from './generated-api.slice';

// enhance code-generated api endpoints
export const apiSlice = generatedApiSlice.enhanceEndpoints({
  addTagTypes: [
    'Session',
    'SessionUser',
    'Products',
    'Product',
    'Prices',
    'Price',
    'Persons',
    'Person',
    'Companies',
    'Company',
    'CompanyUsers',
    'CompanyUser',
    'Buildings',
    'Building',
    'SpaceHierarchy',
    'Spaces',
    'Space',
    'SpaceAreaChanges',
    'SpaceFiles',
    'SpaceFile',
    'CommonAreas',
    'CommonArea',
    'Rooms',
    'Room',
    'ParkingSpots',
    'ParkingSpot',
    'Usages',
    'Usage',
    'Agreements',
    'Agreement',
    'AgreementImportContracts',
    'Contracts',
    'Contract',
    'AgreementProducts',
    'AgreementProduct',
    'AgreementParties',
    'AgreementParty',
    'AgreementSpaces',
    'AgreementSpace',
    'AgreementFiles',
    'AgreementFile',
    'Investments',
    'Investment',
    'InvestmentSpaces',
    'InvestmentSpace',
    'UserPermissions',
    'SubProducts',
    'BuildingEnergySettings',
    'BuildingBidArea',
    'PortfolioOnboardingInvitation',
    'PortfolioTenants',
    'SpaceOccupancies',
    'PortfolioFinancials',
    'Loans',
    'Budgets',
    'FinancialReports',
    'Stakeholders',
  ],
  endpoints: {
    getAuthSession: {
      providesTags: ['Session'],
    },
    getAuthSessionUser: {
      providesTags: ['SessionUser'],
    },

    /* User Permissions */
    getUserPermissions: {
      providesTags: ['UserPermissions'],
    },
    postUserPermissionsSpaces: {
      invalidatesTags: ['UserPermissions'],
    },
    getUserPermissionsSpacesById: {
      providesTags: ['UserPermissions'],
    },
    postUserPermissionsAgreements: {
      invalidatesTags: ['UserPermissions'],
    },
    getUserPermissionsAgreementsById: {
      providesTags: ['UserPermissions'],
    },

    /* Products */
    getProducts: {
      providesTags: ['Products'],
    },
    getProductsById: {
      providesTags: ['Product', 'Prices'],
    },
    postProducts: {
      invalidatesTags: ['Products', 'Product', 'Prices'],
    },
    putProductsById: {
      invalidatesTags: ['Products', 'Product', 'Prices', 'Price'],
    },
    deleteProductsById: {
      invalidatesTags: ['Products'],
    },

    /* Sub-Products */
    getProductsByIdChildren: {
      providesTags: ['SubProducts'],
    },
    postProductsByIdChildren: {
      invalidatesTags: ['SubProducts', 'Products', 'Prices'],
    },
    putProductsByIdChildrenAndChildId: {
      invalidatesTags: ['SubProducts', 'Prices', 'Product'],
    },
    deleteProductsByIdChildrenAndChildId: {
      invalidatesTags: ['SubProducts', 'Products', 'Product', 'Prices'],
    },

    /* Prices */
    getPrices: {
      providesTags: ['Prices'],
    },

    /* Persons */
    getPersons: {
      providesTags: ['Persons'],
    },
    getPersonsById: {
      providesTags: ['Person'],
    },
    putPersonsById: {
      invalidatesTags: ['Persons', 'Person', 'SessionUser'],
    },
    postPersons: {
      invalidatesTags: ['Persons', 'Person'],
    },
    deletePersonsById: {
      invalidatesTags: ['Persons'],
    },

    /* Companies */
    getCompanies: {
      providesTags: ['Companies', 'CompanyUsers'],
    },
    getCompaniesById: {
      providesTags: ['Company', 'CompanyUsers'],
    },
    putCompaniesById: {
      invalidatesTags: ['Companies', 'Company'],
    },
    postCompanies: {
      invalidatesTags: ['Companies', 'Company', 'Stakeholders'],
    },
    getAgreementsStakeholders: {
      providesTags: ['Stakeholders'],
    },
    getCompaniesAtBuildingAddress: {
      providesTags: ['Companies', 'Stakeholders'],
    },
    getCompaniesRoleCounts: {
      providesTags: ['Companies'],
    },
    deleteCompaniesById: {
      invalidatesTags: ['Companies'],
    },

    /* Company Users */
    getOrganizationsByOrganizationIdPersons: {
      providesTags: ['CompanyUsers'],
    },
    putOrganizationsByOrganizationIdPersons: {
      invalidatesTags: ['CompanyUsers', 'CompanyUser'],
    },
    postOrganizationsByOrganizationIdPersons: {
      invalidatesTags: ['CompanyUsers', 'CompanyUser'],
    },
    deleteOrganizationsByOrganizationIdPersonsAndPersonId: {
      invalidatesTags: ['CompanyUsers'],
    },

    /* Space Hierarchy */
    getSpacesByIdHierarchy: {
      providesTags: ['SpaceHierarchy'],
    },

    /* Buildings */
    getBuildings: {
      providesTags: ['Buildings'],
    },
    getBuildingsById: {
      providesTags: ['Building'],
    },
    putBuildingsById: {
      invalidatesTags: ['Buildings', 'Building', 'SpaceHierarchy', 'UserPermissions'],
    },
    postBuildings: {
      invalidatesTags: ['Buildings', 'Building', 'SpaceHierarchy', 'UserPermissions'],
    },
    createBuildingsForTenant: {
      invalidatesTags: ['Buildings', 'Building', 'SpaceHierarchy', 'UserPermissions'],
    },

    /* Spaces */
    getSpaces: {
      providesTags: ['Spaces'],
    },
    getSpacesById: {
      providesTags: ['Space'],
    },
    putSpacesById: {
      // Agreement tags too: agreement views derive common-area allocation from space areas.
      invalidatesTags: [
        'Spaces',
        'Space',
        'Building',
        'Buildings',
        'SpaceHierarchy',
        'SpaceAreaChanges',
        'Agreement',
        'Agreements',
        'AgreementSpaces',
      ],
    },
    postSpaces: {
      invalidatesTags: [
        'Spaces',
        'Space',
        'Building',
        'Buildings',
        'SpaceHierarchy',
        'SpaceAreaChanges',
        'Agreement',
        'Agreements',
        'AgreementSpaces',
      ],
    },
    deleteSpacesById: {
      invalidatesTags: ['Spaces', 'Buildings', 'SpaceHierarchy'],
    },
    putSpacesByIdChildren: {
      invalidatesTags: ['Spaces', 'Buildings', 'Rooms', 'ParkingSpots', 'Space', 'Building', 'Room'], // dont invalidate `'SpaceHierarchy'`
    },

    /* Space Area Changes */
    getSpacesByIdAreaChanges: {
      providesTags: ['SpaceAreaChanges'],
    },
    putSpacesByIdAreaChangesAndChangeIdTag: {
      invalidatesTags: ['SpaceAreaChanges'],
    },

    /* Space Files */
    getSpacesBySpaceIdFiles: {
      providesTags: ['SpaceFiles', 'SpaceFile'],
    },
    postSpacesBySpaceIdFiles: {
      invalidatesTags: ['SpaceFiles', 'SpaceFile', 'Spaces', 'Space', 'Buildings', 'Building', 'SpaceHierarchy'],
    },
    deleteSpacesBySpaceIdFilesAndFileId: {
      invalidatesTags: ['SpaceFiles', 'Spaces', 'Space', 'Buildings', 'Building', 'SpaceHierarchy'],
    },

    /* Commercial Spaces */
    getCommercialSpaces: {
      providesTags: ['Spaces'],
    },
    getCommercialSpacesById: {
      providesTags: ['Space'],
    },

    /* Residential Units */
    getResidentialUnits: {
      providesTags: ['Spaces'],
    },
    getResidentialUnitsById: {
      providesTags: ['Space'],
    },

    /* CommonAreas */
    getCommonAreas: {
      providesTags: ['CommonAreas'],
    },
    getCommonAreasById: {
      providesTags: ['CommonArea'],
    },
    putCommonAreasById: {
      // Agreement tags too: agreement views derive common-area allocation from space areas.
      invalidatesTags: [
        'CommonAreas',
        'CommonArea',
        'Spaces',
        'Space',
        'Building',
        'Buildings',
        'SpaceHierarchy',
        'SpaceAreaChanges',
        'Agreement',
        'Agreements',
        'AgreementSpaces',
      ],
    },
    postCommonAreas: {
      invalidatesTags: [
        'CommonAreas',
        'CommonArea',
        'Spaces',
        'Space',
        'Building',
        'Buildings',
        'SpaceHierarchy',
        'Agreement',
        'Agreements',
        'AgreementSpaces',
      ],
    },

    /* Rooms */
    getRooms: {
      providesTags: ['Rooms'],
    },
    getRoomsById: {
      providesTags: ['Room'],
    },
    putRoomsById: {
      invalidatesTags: ['Rooms', 'Room', 'Spaces', 'Space', 'Building', 'Buildings', 'SpaceHierarchy'],
    },
    postRooms: {
      invalidatesTags: ['Rooms', 'Room', 'Spaces', 'Space', 'Building', 'Buildings', 'SpaceHierarchy'],
    },

    /* Residential Unit Rooms */
    putResidentialUnitsByIdRooms: {
      invalidatesTags: ['Rooms', 'Room', 'SpaceHierarchy'],
    },

    /* ParkingSpots */
    getParkingSpots: {
      providesTags: ['ParkingSpots'],
    },
    getParkingSpotsById: {
      providesTags: ['ParkingSpot'],
    },
    putParkingSpotsById: {
      // Agreement tags too: agreement views derive common-area allocation from space areas.
      invalidatesTags: [
        'ParkingSpots',
        'ParkingSpot',
        'Spaces',
        'Space',
        'Building',
        'Buildings',
        'SpaceHierarchy',
        'SpaceAreaChanges',
        'Agreement',
        'Agreements',
        'AgreementSpaces',
      ],
    },
    postParkingSpots: {
      invalidatesTags: [
        'ParkingSpots',
        'ParkingSpot',
        'Spaces',
        'Space',
        'Building',
        'Buildings',
        'SpaceHierarchy',
        'Agreement',
        'Agreements',
        'AgreementSpaces',
      ],
    },

    /* Usages */
    getUsages: {
      providesTags: ['Usages', 'Usage'],
    },
    getUsagesById: {
      providesTags: ['Usage'],
    },
    putUsagesById: {
      invalidatesTags: ['Usages', 'Usage', 'Spaces', 'Space', 'Buildings', 'Building', 'SpaceHierarchy'],
    },
    postUsages: {
      invalidatesTags: ['Usages', 'Usage', 'Spaces', 'Space', 'Buildings', 'Building', 'SpaceHierarchy'],
    },
    deleteUsagesById: {
      invalidatesTags: ['Usages', 'Spaces', 'Space', 'Buildings', 'Building', 'SpaceHierarchy'],
    },

    /* Agreements */
    getAgreements: {
      providesTags: [
        'Agreements',
        'Agreement',
        'AgreementProducts',
        'AgreementParties',
        'AgreementSpaces',
        'AgreementFiles',
      ],
    },
    getAgreementsById: {
      providesTags: ['Agreement', 'AgreementProducts', 'AgreementParties', 'AgreementSpaces', 'AgreementFiles'],
    },
    putAgreementsById: {
      invalidatesTags: ['Agreements', 'Agreement', 'Spaces', 'Space'],
    },

    postAgreements: {
      invalidatesTags: ['Agreements', 'Agreement'],
    },
    deleteAgreementsById: {
      invalidatesTags: ['Agreements'],
    },
    postAgreementsByIdSyncTripletex: {
      invalidatesTags: ['Agreement'],
    },

    /* Agreement Imports (bulk lease-import wizard) */
    postAgreementImportsBySessionIdRows: {
      invalidatesTags: ['AgreementImportContracts'],
    },
    getAgreementImportsBySessionIdContracts: {
      providesTags: ['AgreementImportContracts'],
    },
    patchAgreementImportsBySessionIdContractsAndContractId: {
      invalidatesTags: ['AgreementImportContracts'],
    },
    postAgreementImportsBySessionIdContractsAndContractIdApprove: {
      invalidatesTags: ['AgreementImportContracts', 'Agreements'],
    },

    /* Agreement Products */
    putAgreementsByAgreementIdProductsAndPriceId: {
      invalidatesTags: ['AgreementProducts', 'AgreementProduct'],
    },
    putAgreementsByAgreementIdProducts: {
      invalidatesTags: ['AgreementProducts', 'AgreementProduct'],
    },
    postAgreementsByAgreementIdProducts: {
      invalidatesTags: ['AgreementProducts', 'AgreementProduct'],
    },
    deleteAgreementsByAgreementIdProductsAndPriceId: {
      invalidatesTags: ['AgreementProducts'],
    },

    /* Agreement Parties */
    putAgreementsByAgreementIdParties: {
      invalidatesTags: ['AgreementParties', 'AgreementParty'],
    },
    postAgreementsByAgreementIdParties: {
      invalidatesTags: ['AgreementParties', 'AgreementParty'],
    },
    deleteAgreementsByAgreementIdPartiesAndLegalPersonId: {
      invalidatesTags: ['AgreementParties'],
    },

    /* Agreement Files */
    getAgreementsByAgreementIdFiles: {
      providesTags: ['AgreementFiles', 'AgreementFile'],
    },
    getAgreementsByAgreementIdFilesAndFileIdDownload: {
      query: (queryArg) => ({
        url: `/agreements/${queryArg.agreementId}/files/${queryArg.fileId}/download`,
        responseHandler: async (response) => {
          if (!response.ok) {
            return Promise.reject({ error: response.statusText, status: response.status });
          }
          return await response.blob();
        },
      }),
      keepUnusedDataFor: 0,
    },
    postAgreementsByAgreementIdFiles: {
      invalidatesTags: ['AgreementFiles', 'AgreementFile'],
    },
    deleteAgreementsByAgreementIdFilesAndFileId: {
      invalidatesTags: ['AgreementFiles'],
    },

    /* Agreement Spaces */
    putAgreementsByAgreementIdSpaces: {
      invalidatesTags: ['AgreementSpaces', 'AgreementSpace'],
    },

    /* Contracts */

    getContracts: {
      providesTags: ['Contracts', 'Contract'],
    },
    postContracts: {
      invalidatesTags: ['Contracts', 'Contract'],
    },
    postContractsByIdSigning: {
      invalidatesTags: ['Contracts', 'Contract'],
    },
    deleteContractsById: {
      invalidatesTags: ['Contracts'],
    },
    deleteContractsByIdSigning: {
      invalidatesTags: ['Contracts'],
    },
    putContractsByIdSignatures: {
      invalidatesTags: ['Contracts', 'Contract'],
    },
    deleteContractsByIdSignatures: {
      invalidatesTags: ['Contracts'],
    },

    /* Building Energy Settings */
    getBuildingsByBuildingIdEnergySettings: {
      providesTags: ['BuildingEnergySettings'],
    },
    putBuildingsByBuildingIdEnergySettings: {
      invalidatesTags: ['BuildingEnergySettings'],
    },

    /* Building Bid Area */
    getBuildingsByBuildingIdBidArea: {
      providesTags: ['BuildingBidArea'],
    },
    putBuildingsByBuildingIdBidArea: {
      invalidatesTags: ['BuildingBidArea'],
    },

    /* Investments */
    getInvestments: {
      providesTags: ['Investments', 'Investment', 'InvestmentSpaces'],
    },
    getInvestmentsById: {
      providesTags: ['Investment', 'InvestmentSpaces'],
    },
    putInvestmentsById: {
      invalidatesTags: ['Investments', 'Investment', 'Spaces', 'Space'],
    },
    postInvestments: {
      invalidatesTags: ['Investments', 'Investment'],
    },
    deleteInvestmentsById: {
      invalidatesTags: ['Investments'],
    },

    /* Investment Spaces */
    putInvestmentsByInvestmentIdSpaces: {
      invalidatesTags: ['InvestmentSpaces', 'InvestmentSpace'],
    },

    /* Data */
    getDataPortfolio: {
      query: () => ({
        url: `/data/portfolio`,
        responseHandler: (response) => response.blob().then((blob) => URL.createObjectURL(blob)),
      }),
    },
    getInvoicesByIdDocument: {
      query: (queryArg) => ({
        url: `/invoices/${queryArg.id}/document`,
        responseHandler: (response) => response.blob().then((blob) => URL.createObjectURL(blob)),
      }),
      keepUnusedDataFor: 0,
    },
    postDataPortfolio: {
      invalidatesTags: ['Buildings', 'Building', 'Spaces', 'Space', 'CommonAreas', 'CommonArea', 'Rooms', 'Room'],
    },

    /* Portfolio Onboarding */
    getPendingPortfolioInvitation: {
      providesTags: ['PortfolioOnboardingInvitation'],
    },
    completePortfolioOnboarding: {
      invalidatesTags: ['PortfolioOnboardingInvitation'],
    },
    createPortfolioOnboardingInvitation: {
      invalidatesTags: ['PortfolioOnboardingInvitation'],
    },

    /* Portfolio Tenants */
    getSpacesByIdPortfolioTenants: {
      providesTags: ['PortfolioTenants'],
    },
    postSpacesByIdPortfolioTenants: {
      invalidatesTags: ['PortfolioTenants', 'Buildings', 'Building', 'Spaces', 'Space'],
    },
    putSpacesByIdPortfolioTenantsAndTenantId: {
      invalidatesTags: ['PortfolioTenants', 'Buildings', 'Building', 'Spaces', 'Space'],
    },
    deleteSpacesByIdPortfolioTenantsAndTenantId: {
      invalidatesTags: ['PortfolioTenants', 'Buildings', 'Building', 'Spaces', 'Space'],
    },

    /* Space Occupancies (framleie / operatører / øvrige brukere) */
    getSpacesByIdOccupancies: {
      providesTags: ['SpaceOccupancies'],
    },
    postSpacesByIdOccupancies: {
      invalidatesTags: ['SpaceOccupancies'],
    },
    putSpacesByIdOccupanciesAndOccupancyId: {
      invalidatesTags: ['SpaceOccupancies'],
    },
    deleteSpacesByIdOccupanciesAndOccupancyId: {
      invalidatesTags: ['SpaceOccupancies'],
    },

    /* Portfolio Financials */
    getSpacesByIdPortfolioFinancials: {
      providesTags: ['PortfolioFinancials'],
    },
    putSpacesByIdPortfolioFinancials: {
      invalidatesTags: ['PortfolioFinancials'],
    },
    postSpacesByIdPortfolioFinancialsValuationReports: {
      invalidatesTags: ['PortfolioFinancials'],
    },
    deleteSpacesByIdPortfolioFinancialsValuationReportsAndReportId: {
      invalidatesTags: ['PortfolioFinancials'],
    },

    /* Loans */
    getSpacesByIdLoans: {
      providesTags: ['Loans'],
    },
    postSpacesByIdLoans: {
      invalidatesTags: ['Loans'],
    },
    putSpacesByIdLoansAndLoanId: {
      invalidatesTags: ['Loans'],
    },
    deleteSpacesByIdLoansAndLoanId: {
      invalidatesTags: ['Loans'],
    },

    /* Financial Reports (register-based economy) */
    getSpacesByIdFinancialReports: {
      providesTags: ['FinancialReports'],
    },
    postSpacesByIdFinancialReportsAndYear: {
      // The generated client serializes the body as JSON, which drops the file. Build multipart
      // FormData instead so the [FromForm] IFormFile binds on the backend.
      query: (queryArg) => {
        const formData = new FormData();
        if (queryArg.body.File) {
          const file = queryArg.body.File as File;
          formData.append('File', file, file.name);
        }
        if (queryArg.body.ErpSystem) {
          formData.append('ErpSystem', queryArg.body.ErpSystem);
        }
        if (queryArg.body.MonthsCovered != null) {
          formData.append('MonthsCovered', String(queryArg.body.MonthsCovered));
        }
        return {
          url: `/spaces/${queryArg.id}/financial-reports/${queryArg.year}`,
          method: 'POST',
          body: formData,
          // Overriding `query` replaces the generated builder wholesale, so the generated
          // `params` must be repeated here or the quarter/accumulated selection is dropped and
          // a quarterly upload is stored as the annual report.
          params: { quarter: queryArg.quarter, accumulated: queryArg.accumulated },
        };
      },
      invalidatesTags: ['FinancialReports'],
    },
    deleteSpacesByIdFinancialReportsAndYear: {
      invalidatesTags: ['FinancialReports'],
    },
    // The register-based economy report aggregates the latest uploaded financial report per
    // building, so it must refetch whenever a CSV is uploaded, replaced or removed.
    getReportingPortfolioOverview: {
      providesTags: ['FinancialReports'],
    },
    // The financing report ("Gjeld & soliditet") derives its balance-sheet figures from the latest
    // uploaded saldobalanse per company, so it refetches on the same CSV changes.
    getReportingFinancing: {
      providesTags: ['FinancialReports'],
    },
    getSpacesByIdFinancialReportsAndYearFile: {
      query: (queryArg) => ({
        url: `/spaces/${queryArg.id}/financial-reports/${queryArg.year}/file`,
        // Same as the upload above: the generated `params` must be repeated when `query` is
        // overridden, otherwise a quarterly row downloads the annual file.
        params: { quarter: queryArg.quarter },
        responseHandler: async (response) => {
          if (!response.ok) {
            return Promise.reject({ error: response.statusText, status: response.status });
          }
          return await response.blob();
        },
      }),
      keepUnusedDataFor: 0,
    },

    /* Budgets */
    getSpacesByIdBudgets: {
      providesTags: ['Budgets'],
    },
    getSpacesByIdBudgetsAndYear: {
      providesTags: ['Budgets'],
    },
    putSpacesByIdBudgetsAndYear: {
      invalidatesTags: ['Budgets'],
    },
    deleteSpacesByIdBudgetsAndYear: {
      invalidatesTags: ['Budgets'],
    },

    /* Statistics */
    getStatisticsVacancy: {
      providesTags: [
        'Buildings',
        'Building',
        'Spaces',
        'Space',
        'CommonAreas',
        'CommonArea',
        'Rooms',
        'Room',
        'SpaceHierarchy',
      ],
    },
  },
});

// searchBuildings is now auto-generated from swagger; see generated-api.slice.ts

export const apiMiddleware = apiSlice.middleware;

export const apiReducer = apiSlice.reducer;

export const API_FEATURE_KEY = apiSlice.reducerPath;

export type ApiQueryError = Pick<FetchBaseQueryError, 'status'> & {
  data: ProblemDetails;
};

export type QueryError = FetchBaseQueryError | ApiQueryError | undefined;

export type QueryMeta = {
  baseQueryMeta?: FetchBaseQueryMeta;
  arg?: {
    endpointName?: string;
  };
};

// Any type of error could occur - Isn't related to http errors but can be any error from rtk query when a query was rejected, which can also be because a condition was not met or a call was aborted
// Example for an rtk query error: https://stackoverflow.com/questions/69789058/what-does-this-error-mean-in-redux-toolkit
export type QueryPayloadAction = PayloadAction<QueryError, string, QueryMeta, SerializedError>;

export function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
  return typeof error === 'object' && error != null && 'status' in error;
}

export function isApiQueryError(error: unknown): error is ApiQueryError {
  return (
    typeof error === 'object' &&
    error != null &&
    'status' in error &&
    'data' in error &&
    (error as any).data != null &&
    typeof (error as any).data === 'object' &&
    'detail' in (error as any).data
  );
}

export type QueryErrorStatus = FetchBaseQueryError['status'] | ApiQueryError['status'] | undefined;
export type QueryErrorMessage = string | undefined;

/**
 * Receive either `FetchBaseQueryError` status or http status number if existing in the action
 */
export function getQueryErrorStatus(action: QueryPayloadAction): QueryErrorStatus {
  const baseQueryMetaResponse = action.meta.baseQueryMeta?.response;
  const payload = action.payload;

  if (payload != null && typeof payload === 'object' && 'status' in payload) {
    return payload.status;
  }

  if (baseQueryMetaResponse != null && typeof baseQueryMetaResponse === 'object' && 'status' in baseQueryMetaResponse) {
    return baseQueryMetaResponse.status;
  }

  return undefined;
}

/**
 * Receive error message from query rejection but only if it's a real error
 */
export function getQueryErrorMessage(action: QueryPayloadAction): QueryErrorMessage {
  const baseQueryMetaResponse = action.meta.baseQueryMeta?.response;
  const payload = action.payload;
  // if either no payload exist and if no baseQueryMetaResponse exists (if response is null then baseQueyMeta is corrupted)
  // this will be the case if its a simple rejection from rtk query which means its not an error but conditions might not met or its aborted
  if (payload == null && baseQueryMetaResponse == null) return;

  const status = getQueryErrorStatus(action);

  // if status is a number its a http status number
  if (typeof status === 'number') {
    let message: string | undefined;

    // if its an error with defined response from api
    if (isApiQueryError(payload)) {
      const apiError = payload.data;
      message = apiError?.detail ?? apiError?.title ?? undefined;
    } else {
      // otherwise take the `statusText` from the `baseQueryMetaResponse` (e. g. "Not found")
      message = baseQueryMetaResponse?.statusText;
    }

    // if no message was set yet and the error is a fetchBaseQuery error
    if (!message && isFetchBaseQueryError(payload)) {
      const baseQueryError = 'error' in payload ? payload.error : undefined;
      message = baseQueryError;

      if (!message && payload.data) {
        message = JSON.stringify(payload.data);
      }
    }

    if (status === 401) {
      return message ?? 'Unauthorized';
    }

    if (status >= 400 && status < 500) {
      const problemDetails = (payload?.data as ProblemDetails) ?? {};
      const detail = problemDetails?.['detail'];

      // Prefer structured error codes (translated) over generic detail messages
      const errors = problemDetails?.['errors'] ?? {};
      const errorsKeys = Object.keys(errors);

      if (errorsKeys.length > 0) {
        const firstErrorMessages = errors[errorsKeys[0]];
        const firstErrorCode = Array.isArray(firstErrorMessages) ? firstErrorMessages[0] : firstErrorMessages;
        const translationKey = `server.${firstErrorCode}`;
        const translated = i18n.t(translationKey, { ns: 'validation' });

        // If the translation resolved (not the same as the key), use it
        if (translated !== translationKey) {
          return translated;
        }

        // Otherwise fall back to detail field or the error key itself (may be a human-readable message)
        if (detail && detail !== 'Bad request' && detail !== 'Validation error') {
          return detail;
        }

        return errorsKeys[0];
      }

      // Fall back to detail field if no structured errors exist
      if (detail && detail !== 'Bad request' && detail !== 'Validation error') {
        return detail;
      }

      return message;
    }

    if (status >= 500 && status < 600) {
      return message;
    }

    return `Unknown API error: ${status}`;
  }

  // if its fetchBaseQuery error status
  if (typeof status === 'string' && !!status) {
    return status;
  }

  // if its any other action with rejectedValue and and error message
  if (isRejectedWithValue(action) && !!action?.error?.message) {
    return action.error.message;
  }

  // otherwise don't return any message
  return;
}
